import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import theme from './theme';
import StorePage from './pages/store';
import CartPage from './pages/store/cart';
import OrdersPage from './pages/store/orders';
import PersonalizePage from './pages/store/personalize/[id]';
import TermsPage from './pages/store/terms';
import CreatePage from './pages/store/create';
import CreationsPage from './pages/store/creations';

// Checkout pulls in @stripe/stripe-js + @stripe/react-stripe-js, which inject Stripe.js on load.
// Code-split it so Stripe is NEVER on the browse critical path — it loads only when a shopper reaches
// checkout (store-home perf, audit 2026-06-26).
const CheckoutPage = lazy(() => import('./pages/store/checkout'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Back-compat for the old `/store`-prefixed URLs. One catch-all rule client-redirects `/store` and
// every deep `/store/...` path to its flattened equivalent, preserving the remaining path + query.
// (Soft redirect; true 301s for SEO belong at the host fronting thecardstore.co — owner note.)
export const StoreRedirect = () => {
  const location = useLocation();
  const flattened = location.pathname.replace(/^\/store/, '') || '/';
  return <Navigate replace to={`${flattened}${location.search}`} />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<StorePage />} />
            <Route path="/create" element={<CreatePage />} />
            <Route path="/creations" element={<CreationsPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/personalize/:id" element={<PersonalizePage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* Legacy /store and /store/* → flattened path (query preserved). */}
            <Route path="/store/*" element={<StoreRedirect />} />
            <Route path="/store" element={<StoreRedirect />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </Router>
    </ThemeProvider>
  );
}

export default App;
